import { createSelector } from 'reselect';
import {
  DeviceQuantityMap,
  OBAvailableHardware,
  OBHardwareType,
  OBIdToScannerMap,
  OBIdToSensorMap,
} from '../types';
import { onboardingSelector } from '../reducers/onboarding';
import onboardingUtils from '../reducers/onboarding/utils';

const getConfiguredQuantity = (
  type: OBHardwareType,
  configuredMap: OBIdToScannerMap | OBIdToSensorMap,
  idToIgnore?: string,
  availableHardware?: OBAvailableHardware | undefined,
): DeviceQuantityMap => {
  const quantityMap: DeviceQuantityMap = {};
  Object.keys(configuredMap)
    .filter((uuid) => uuid !== idToIgnore)
    .forEach((uuid) => {
      const device = configuredMap[uuid];
      let model = device.model;
      if (availableHardware) {
        const hard = onboardingUtils.getHardwareInfoByHardwareKey(
          availableHardware[type],
          device.model,
        );
        model = hard?.allotment_key ?? device.model;
      }
      quantityMap[model] = (quantityMap[model] ?? 0) + 1;
    });
  return quantityMap;
};

const getQuantityDiff = (
  quantity1: DeviceQuantityMap,
  quantity2: DeviceQuantityMap,
) => {
  const quantityDiff: DeviceQuantityMap = {};
  Object.entries(quantity1).forEach(([model, purchasedCount]) => {
    const diff = purchasedCount - (quantity2[model] ?? 0);
    if (diff > 0) {
      quantityDiff[model] = diff;
    }
  });
  return quantityDiff;
};

// memoized selectors

const getConfiguredQuantityMap = createSelector(
  [
    onboardingSelector.getSensors,
    onboardingSelector.getScanners,
    onboardingSelector.getAvailableHardware,
  ],
  (configuredSensorMap, configuredScannerMap, availableHardware) => {
    return {
      ...getConfiguredQuantity(
        OBHardwareType.SENSOR,
        configuredSensorMap,
        '',
        availableHardware,
      ),
      ...getConfiguredQuantity(OBHardwareType.SCANNER, configuredScannerMap),
    };
  },
);

const getIncompleteHardware = (type: OBHardwareType, idToIgnore?: string) =>
  createSelector(
    [
      getHardwareAllotment(type),
      onboardingSelector.getSensors,
      onboardingSelector.getScanners,
      onboardingSelector.getAvailableHardware,
    ],
    (
      allotment,
      configuredSensorMap,
      configuredScannerMap,
      availableHardware,
    ) => {
      const configuredQuantity = getConfiguredQuantity(
        type,
        type === OBHardwareType.SENSOR
          ? configuredSensorMap
          : configuredScannerMap,
        idToIgnore,
        availableHardware,
      );
      return getQuantityDiff(allotment, configuredQuantity);
    },
  );

const getHardwareAllotment = (type: OBHardwareType) =>
  createSelector(
    [
      onboardingSelector.getHardwareQuantity,
      onboardingSelector.getAvailableHardware,
    ],
    (allotment, hardwareInfo) => {
      const hardWareAllotment: DeviceQuantityMap = {};
      if (!allotment) {
        return hardWareAllotment;
      }
      for (let [model, quantity] of Object.entries(allotment)) {
        if (
          hardwareInfo[type] &&
          hardwareInfo[type].find((sensor) => sensor.hardware_key === model)
        ) {
          hardWareAllotment[model] = quantity;
        }
      }
      return hardWareAllotment;
    },
  );

const getPurchasedGenericSeries = createSelector(
  [onboardingSelector.getHardwareQuantity],
  (allotment) => {
    const seriesAllotment = { ...allotment };
    if (!seriesAllotment) {
      return {};
    }
    for (const key in seriesAllotment) {
      if (seriesAllotment.hasOwnProperty(key) && !key.includes('series')) {
        delete seriesAllotment[key];
      }
    }
    return seriesAllotment;
  },
);

const getRemainingGenericSeries = createSelector(
  [onboardingSelector.getSensors, getPurchasedGenericSeries],
  (sensors, purchasedQuantity) => {
    const remaining: DeviceQuantityMap = { ...purchasedQuantity };
    Object.values(sensors).forEach((sensor) => {
      if (sensor.series) {
        if (remaining[sensor.series] && remaining[sensor.series] > 0) {
          remaining[sensor.series]--;
        }
      }
    });
    return remaining;
  },
);

const getConfiguredGenericSeries = createSelector(
  [onboardingSelector.getSensors],
  (sensors) => {
    const configured: DeviceQuantityMap = {};
    Object.values(sensors).forEach((sensor) => {
      if (sensor.series) {
        configured[sensor.series]
          ? configured[sensor.series]++
          : (configured[sensor.series] = 1);
      }
    });
    return configured;
  },
);

export const memoizedHardwareSelectors = {
  getConfiguredQuantityMap,
  getIncompleteHardware,
  getConfiguredGenericSeries,
  getRemainingGenericSeries,
  getPurchasedGenericSeries,
};

export const memoizedHardwareAllotmentSelectors = {
  [OBHardwareType.SENSOR]: getHardwareAllotment(OBHardwareType.SENSOR),
  [OBHardwareType.SCANNER]: getHardwareAllotment(OBHardwareType.SCANNER),
};
